import { ArrowRightIcon, CameraIcon, CheckIcon, ExclamationTriangleIcon, MapPinIcon, StarIcon as OutlineStarIcon, PencilIcon, PhotoIcon, SparklesIcon, UsersIcon } from "@heroicons/react/24/outline";
import { StarIcon as SolidStarIcon } from "@heroicons/react/24/solid";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "../../utils/constants.js";
import PlaceAutocompleteInput from "../PlacesAutocomplete/PlacesAutocomplete";
import "./PhotoImportFlow.css";

// StarDisplay component (moved here to match SingleRecommendationForm)
const StarDisplay = ({ active, onClick }) => {
    const eventHandlers = {
        onClick: onClick,
        onTouchEnd: (e) => {
            e.preventDefault();
            onClick();
        },
    };

    if (active) {
        return (
            <SolidStarIcon
                className="star-icon filled"
                {...eventHandlers}
                aria-hidden="true"
            />
        );
    }
    return (
        <OutlineStarIcon
            className="star-icon"
            {...eventHandlers}
            aria-hidden="true"
        />
    );
};

const PhotoImportFlow = ({ userEmail, userId, navigate }) => {
  const [currentStep, setCurrentStep] = useState("upload");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [extractedPlaces, setExtractedPlaces] = useState([]);
  const [reviews, setReviews] = useState({});
  const [selectedPlaces, setSelectedPlaces] = useState(new Set());
  const [uploadStatus, setUploadStatus] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [editingPlaceId, setEditingPlaceId] = useState(null);
  const [editingPlaceName, setEditingPlaceName] = useState("");
  const [submissionResult, setSubmissionResult] = useState(null);
  const [userTrustCircles, setUserTrustCircles] = useState([]);
  const [trustCirclesLoading, setTrustCirclesLoading] = useState(false);
  const [trustCirclesError, setTrustCirclesError] = useState("");
  const [globalVisibility, setGlobalVisibility] = useState('public');
  const [globalTrustCircleIds, setGlobalTrustCircleIds] = useState([]);
  const [individualVisibilities, setIndividualVisibilities] = useState({});
  const [individualTrustCircleIds, setIndividualTrustCircleIds] = useState({});
  const [selectedPhotos, setSelectedPhotos] = useState(new Set()); // Track which photos to include
  const [existingSessions, setExistingSessions] = useState([]);
  const [showExistingSessions, setShowExistingSessions] = useState(false);
  const [selectedSessionsToDelete, setSelectedSessionsToDelete] = useState(new Set());
  const [isDeletingSessions, setIsDeletingSessions] = useState(false);
  const fileInputRef = useRef(null);
  const dropzoneRef = useRef(null);

  // Memoized image URL generation to prevent expensive re-computations
  const getImageSrc = useCallback((photo) => {
    if (!photo) return null;
    if (typeof photo === 'string') return photo;
    if (photo.url) return photo.url;
    if (photo.gcs_path) {
      // Determine which bucket to use based on the path
      let bucketName = 'tried-and-trusted-temp-photos'; // default to temp bucket
      if (photo.gcs_path.includes('reviews/')) {
        bucketName = 'tried-and-trusted-images-production';
      }
      return `https://storage.googleapis.com/${bucketName}/${photo.gcs_path}`;
    }
    if (photo.data && photo.contentType) {
      const bufferData = photo.data.data;
      if (!bufferData) return null;
      try {
        const base64String = btoa(
          new Uint8Array(bufferData).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ''
          )
        );
        return `data:${photo.contentType};base64,${base64String}`;
      } catch (e) {
        console.error("Error converting image buffer for display:", e);
        return null;
      }
    }
    return null;
  }, []);

  // Fetch user's trust circles only when needed (like extension)
  const fetchTrustCircles = async () => {
    if (!userId || userTrustCircles.length > 0) return; // Don't fetch if already loaded
    
    setTrustCirclesLoading(true);
    setTrustCirclesError("");
    
    try {
      const response = await fetch(`${API_URL}/api/recommendations/photo-import/trust-circles/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const trustCircles = await response.json();
        setUserTrustCircles(trustCircles);
        console.log(`✅ Fetched ${trustCircles.length} trust circles for user`);
      } else {
        console.warn(`⚠️ Failed to fetch trust circles: ${response.status}`);
        setTrustCirclesError("Failed to load trust circles");
      }
    } catch (error) {
      console.error('❌ Error fetching trust circles:', error);
      setTrustCirclesError("Error loading trust circles");
    } finally {
      setTrustCirclesLoading(false);
    }
  };

  // Check for existing active sessions when component mounts
  useEffect(() => {
    const checkExistingSessions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/recommendations/photo-import/active-sessions?user_email=${encodeURIComponent(userEmail)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.activeSessions && data.activeSessions.length > 0) {
            setExistingSessions(data.activeSessions);
            setShowExistingSessions(true);
            console.log(`Found ${data.activeSessions.length} active sessions with remaining photos`);
          } else {
            // No existing sessions, don't show modal
            setShowExistingSessions(false);
            console.log('No existing sessions found');
          }
        }
      } catch (error) {
        console.error('Error checking for existing sessions:', error);
      }
    };

    if (userEmail) {
      checkExistingSessions();
    }
  }, [userEmail]);

  // Add smooth transitions between steps
  useEffect(() => {
    const container = document.querySelector('.photo-import-container');
    if (container) {
      container.classList.add('fade-in');
      setTimeout(() => {
        container.classList.remove('fade-in');
      }, 500);
    }
  }, [currentStep]);

  // Drag and drop functionality
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const limitedFiles = imageFiles.slice(0, 10000);
    setSelectedFiles(limitedFiles);
  }, []);

  // Function to fetch actual uploaded photos from the session
  const fetchSessionPhotos = async (sessionId) => {
    try {
      // Fetch both candidate photos and photos needing location
      const [candidatesResponse, needsLocationResponse] = await Promise.all([
        fetch(`${API_URL}/api/recommendations/temp-photos/candidates/${sessionId}?user_email=${encodeURIComponent(userEmail)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }),
        fetch(`${API_URL}/api/recommendations/temp-photos/no-place-found/${sessionId}?user_email=${encodeURIComponent(userEmail)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        })
      ]);

      let candidates = [];
      let needsLocationPhotos = [];

      // Process candidates response
      if (candidatesResponse.ok) {
        const candidatesData = await candidatesResponse.json();
        candidates = candidatesData.candidates || [];
      } else {
        console.warn(`⚠️ Failed to fetch candidates: ${candidatesResponse.status}`);
      }

      // Process needs location response
      if (needsLocationResponse.ok) {
        const needsLocationData = await needsLocationResponse.json();
        needsLocationPhotos = needsLocationData.photos || [];
      } else {
        console.warn(`⚠️ Failed to fetch photos needing location: ${needsLocationResponse.status}`);
      }

      // Combine and transform all photos
      const allPhotos = [...candidates, ...needsLocationPhotos];
      
      // Initialize selectedPhotos with all photos selected by default
      const initialSelectedPhotos = new Set(allPhotos.map(photo => photo.id));
      setSelectedPhotos(initialSelectedPhotos);
      
      const transformedPlaces = allPhotos.map(photo => {
        // Use final path if available, otherwise fall back to temp path
        const photoPath = photo.gcsFinalPath || photo.gcsTempPath;
        
        // Pre-compute image URL to avoid expensive re-computations
        const imageUrl = photoPath ? getImageSrc({ gcs_path: photoPath }) : null;
        console.log(`[DEBUG] Photo ${photo.id}: photoPath=${photoPath}, imageUrl=${imageUrl}`);
        
        // Determine if this photo needs location input (handle all statuses that need location)
        const needsLocationInput = photo.processingStatus === 'needs_location' || 
                                  photo.processingStatus === 'no_place_found' || 
                                  photo.needsLocationInput;
        
        // Determine if this photo is ready for submission (has business info)
        const isReadyForSubmission = photo.processingStatus === 'candidate_for_review' || 
                                    photo.processingStatus === 'pending_submission';
        
        const place = {
          id: photo.id,
          name: photo.businessName || (needsLocationInput ? 'Add Location' : 'Unknown Place'),
          address: photo.streetAddress ? `${photo.streetAddress}` : (needsLocationInput ? 'Location needed' : 'Address not available'),
          category: 'Unknown Category', // Not provided by API
          photos: imageUrl ? [{
            id: photo.id,
            url: imageUrl // Use pre-computed URL instead of gcs_path
          }] : [],
          confidence: 0.8, // Default confidence
          isDuplicate: false, // Not provided by API
          existingReview: false, // Not provided by API
          dateOfRecommendation: photo.dateOfRecommendation || new Date().toISOString(), // Use date_of_recommendation
          latitude: photo.latitude,
          longitude: photo.longitude,
          googlePlaceId: photo.googlePlaceId,
          originalFilename: photo.originalFilename,
          gcsTempPath: photo.gcsTempPath, // Store for potential updates
          gcsFinalPath: photo.gcsFinalPath, // Store final path if available
          starRating: 0, // Always start with 0 rating for user interaction
          needsLocationInput: needsLocationInput, // Flag for photos needing location input
          isReadyForSubmission: isReadyForSubmission, // Flag for photos ready for submission
          photoId: photo.id, // Store the photo ID for tracking selection
        };
        return place;
      });
      
      return transformedPlaces;
    } catch (error) {
      console.error('❌ Error fetching session photos:', error);
      return [];
    }
  };

  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const limitedFiles = imageFiles.slice(0, 10000);
    setSelectedFiles(limitedFiles);
    
    // Add visual feedback
    if (limitedFiles.length > 0) {
      const dropzone = document.querySelector('.upload-dropzone');
      if (dropzone) {
        dropzone.classList.add('scale-in');
        setTimeout(() => {
          dropzone.classList.remove('scale-in');
        }, 500);
      }
    }
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setUploadStatus("Please select at least one photo");
      return;
    }

    setIsProcessing(true);
    setCurrentStep("processing");
    setUploadProgress(0);
    setProcessingProgress(0);
    setUploadStatus("Uploading photos...");

    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify({
        user_email: userEmail,
        user_id: userId,
        photo_count: selectedFiles.length,
        existing_session_id: sessionId // Pass existing session ID if available
      }));

      selectedFiles.forEach((file) => {
        formData.append('photos', file);
      });

      // Enhanced upload progress simulation with better timing
      const uploadInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(uploadInterval);
            return 100;
          }
          return prev + Math.random() * 15 + 5;
        });
      }, 200);

      const endpoint = selectedFiles.length > 100 
        ? `${API_URL}/api/recommendations/upload-massive-photos`
        : `${API_URL}/api/recommendations/upload-photos`;

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }

      const result = await response.json();
      setSessionId(result.sessionId);

      // Enhanced processing progress simulation
      const processingInterval = setInterval(() => {
        setProcessingProgress(prev => {
          if (prev >= 100) {
            clearInterval(processingInterval);
            return 100;
          }
          return prev + Math.random() * 20 + 10;
        });
      }, 300);

      // Trigger photo processing
      try {
        const processResponse = await fetch(`${API_URL}/api/recommendations/temp-photos/process-session/${result.sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId
          })
        });

        if (processResponse.ok) {
          const processResult = await processResponse.json();
          console.log('✅ Photo processing complete:', processResult);
          
          // Wait a moment for processing to complete, then fetch candidates
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          // Fetch actual uploaded photos from the session with retry
          let actualPlaces = [];
          let retryCount = 0;
          const maxRetries = 3;
          
          while (retryCount < maxRetries) {
            try {
              actualPlaces = await fetchSessionPhotos(result.sessionId);
              if (actualPlaces.length > 0) {
                break;
              }
              console.log(`🔄 No candidates found, retrying... (${retryCount + 1}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, 1000));
              retryCount++;
            } catch (error) {
              console.error(`❌ Error fetching candidates (attempt ${retryCount + 1}):`, error);
              retryCount++;
              if (retryCount < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          }
          
          setExtractedPlaces(actualPlaces);
          
          // Automatically select all non-duplicate places
          const nonDuplicatePlaces = actualPlaces.filter(place => !place.isDuplicate);
          const placeIds = nonDuplicatePlaces.map(place => place.id);
          setSelectedPlaces(new Set(placeIds));
          
          // Initialize reviews for all selected places
          const initialReviews = {};
          placeIds.forEach(placeId => {
            const place = actualPlaces.find(p => p.id === placeId);
            initialReviews[placeId] = { 
              placeId, 
              rating: 0, 
              message: "" 
            };
          });
          setReviews(initialReviews);
          
          setIsProcessing(false);
          setCurrentStep("review");
          
          // Show message if no places were found
          if (actualPlaces.length === 0) {
            console.log('⚠️ No places found in photos. This could be because:');
            console.log('  - Photos don\'t contain location data (GPS)');
            console.log('  - Photos don\'t contain recognizable businesses');
            console.log('  - Processing failed or is still in progress');
          }
        } else {
          throw new Error('Processing failed');
        }
      } catch (processError) {
        console.error('❌ Error triggering photo processing:', processError);
        // Try to fetch session photos even if processing failed
        const actualPlaces = await fetchSessionPhotos(result.sessionId);
        setExtractedPlaces(actualPlaces);
        
        // Automatically select all non-duplicate places
        const nonDuplicatePlaces = actualPlaces.filter(place => !place.isDuplicate);
        const placeIds = nonDuplicatePlaces.map(place => place.id);
        setSelectedPlaces(new Set(placeIds));
        
        // Initialize reviews for all selected places
        const initialReviews = {};
        placeIds.forEach(placeId => {
          initialReviews[placeId] = { placeId, rating: 0, message: "" };
        });
        setReviews(initialReviews);
        
        setIsProcessing(false);
        setCurrentStep("review");
        
        // Show message if no places were found
        if (actualPlaces.length === 0) {
          console.log('⚠️ No places found in photos. This could be because:');
          console.log('  - Photos don\'t contain location data (GPS)');
          console.log('  - Photos don\'t contain recognizable businesses');
          console.log('  - Processing failed or is still in progress');
        }
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`Upload failed: ${error.message}`);
      setIsProcessing(false);
      setCurrentStep("upload");
    }
  };

  const handlePlaceToggle = (placeId) => {
    const newSelected = new Set(selectedPlaces);
    if (newSelected.has(placeId)) {
      newSelected.delete(placeId);
      const newReviews = { ...reviews };
      delete newReviews[placeId];
      setReviews(newReviews);
    } else {
      newSelected.add(placeId);
      const place = extractedPlaces.find(p => p.id === placeId);
      setReviews((prev) => ({
        ...prev,
        [placeId]: { 
          placeId, 
          rating: 0, 
          message: "" 
        },
      }));
    }
    setSelectedPlaces(newSelected);
  };

  const handleRatingChange = (placeId, rating) => {
    setReviews((prev) => ({
      ...prev,
      [placeId]: { ...prev[placeId], rating },
    }));
  };

  const handleMessageChange = (placeId, message) => {
    setReviews((prev) => ({
      ...prev,
      [placeId]: { ...prev[placeId], message },
    }));
  };

  const handleSubmit = async () => {
    
    
    if (selectedPlaces.size === 0) {
              return;
      }

    setCurrentStep("complete");
    
    try {
      // Prepare recommendations data
      const recommendations = Array.from(selectedPlaces).map(placeId => {
        const place = extractedPlaces.find(p => p.id === placeId);
        const review = reviews[placeId];
        
        // Determine visibility and trust circles for this recommendation
        let visibility = globalVisibility;
        let trustCircleIds = globalTrustCircleIds;
        
        if (globalVisibility === 'per-recommendation') {
          visibility = individualVisibilities[placeId] || 'public';
          trustCircleIds = individualTrustCircleIds[placeId] || [];
        }
        
        // Check if the photo should be included in the recommendation
        const includePhoto = selectedPhotos.has(place.photoId);
        
        return {
          photoId: place.id,
          businessName: place.name,
          address: place.address,
          rating: review?.rating || 0,
          message: review?.message || "", // Message is now optional
          googlePlaceId: place.googlePlaceId,
          latitude: place.latitude,
          longitude: place.longitude,
          dateOfRecommendation: place.dateOfRecommendation,
          visibility,
          trustCircleIds,
          includePhoto // Flag to indicate whether to include the photo
        };
      });



      const response = await fetch(`${API_URL}/api/recommendations/photo-import/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          userEmail,
          recommendations,
          globalVisibility,
          globalTrustCircleIds
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Submission failed');
      }

      const result = await response.json();
      console.log('✅ Recommendations submitted successfully:', result);
      
      // Store submission result
      setSubmissionResult(result);
      
      // Show success message
      if (result.errors && result.errors.length > 0) {
        console.warn('Some recommendations failed to submit:', result.errors);
      }

    } catch (error) {
      console.error('❌ Error submitting recommendations:', error);
      // You could show an error message to the user here
      // For now, we'll still show the complete step
    }
  };

  const canProceed = selectedPlaces.size > 0 && Array.from(selectedPlaces).every((id) => {
    const place = extractedPlaces.find(p => p.id === id);
    const review = reviews[id];
    
    // Check if photo needs location input
    if (place?.needsLocationInput) {
      // For photos needing location, check that they have location data and a rating
      return place.latitude && place.longitude && place.googlePlaceId && review?.rating > 0;
    } else if (place?.isReadyForSubmission) {
      // For photos ready for submission, just check rating - message is optional
      return review?.rating > 0;
    } else {
      // For other photos, just check rating
      return review?.rating > 0;
    }
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Handle place editing
  const handleEditPlace = (placeId) => {
    const place = extractedPlaces.find(p => p.id === placeId);
    if (place) {
      setEditingPlaceId(placeId);
      setEditingPlaceName(place.name);
    }
  };

  const handlePlaceNameChange = (value) => {
    setEditingPlaceName(value);
  };

  const handlePlaceSelect = async (place) => {
    try {
      // Determine which photo ID to update
      const photoId = editingPlaceId || place.photoId; // Use editingPlaceId if editing, otherwise use place.photoId
      
      if (!photoId) {
        console.error('❌ No photo ID available for place selection');
        return;
      }

      // Update the place in the extractedPlaces array
      const updatedPlace = {
        name: place.details?.displayName?.text || place.businessName || place.description || editingPlaceName,
        address: place.addressInfo?.street_address ? 
          `${place.addressInfo.street_address}${place.addressInfo.city ? `, ${place.addressInfo.city}` : ''}${place.addressInfo.state ? `, ${place.addressInfo.state}` : ''}` : 
          'Address not available',
        googlePlaceId: place.google_place_id,
        latitude: place.latitude,
        longitude: place.longitude,
      };

      // Call API to update the place information
      const response = await fetch(`${API_URL}/api/recommendations/temp-photos/${photoId}/place-info?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessName: updatedPlace.name,
          streetAddress: place.addressInfo?.street_address || '',
          googlePlaceId: updatedPlace.googlePlaceId,
          latitude: updatedPlace.latitude,
          longitude: updatedPlace.longitude,
          star_rating: reviews[photoId]?.rating || 0,
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Updated place info:', result);
        
        // Update the place in the extractedPlaces array
        setExtractedPlaces(prevPlaces => 
          prevPlaces.map(p => 
            p.id === photoId 
              ? {
                  ...p,
                  name: updatedPlace.name,
                  address: updatedPlace.address,
                  googlePlaceId: updatedPlace.googlePlaceId,
                  latitude: updatedPlace.latitude,
                  longitude: updatedPlace.longitude,
                  needsLocationInput: false, // Mark as no longer needing location input
                }
              : p
          )
        );
      } else {
        console.error('❌ Failed to update place info:', response.status);
        // Still update the frontend even if API call fails
        setExtractedPlaces(prevPlaces => 
          prevPlaces.map(p => 
            p.id === photoId 
              ? {
                  ...p,
                  name: updatedPlace.name,
                  address: updatedPlace.address,
                  googlePlaceId: updatedPlace.googlePlaceId,
                  latitude: updatedPlace.latitude,
                  longitude: updatedPlace.longitude,
                  needsLocationInput: false, // Mark as no longer needing location input
                }
              : p
          )
        );
      }
    } catch (error) {
      console.error('❌ Error updating place info:', error);
      // Still update the frontend even if API call fails
      const photoId = editingPlaceId || place.photoId;
      if (photoId) {
        setExtractedPlaces(prevPlaces => 
          prevPlaces.map(p => 
            p.id === photoId 
              ? {
                  ...p,
                  name: place.details?.displayName?.text || place.businessName || place.description || editingPlaceName,
                  address: place.addressInfo?.street_address ? 
                    `${place.addressInfo.street_address}${place.addressInfo.city ? `, ${place.addressInfo.city}` : ''}${place.addressInfo.state ? `, ${place.addressInfo.state}` : ''}` : 
                    p.address,
                  googlePlaceId: place.google_place_id || p.googlePlaceId,
                  latitude: place.latitude || p.latitude,
                  longitude: place.longitude || p.longitude,
                  needsLocationInput: false, // Mark as no longer needing location input
                }
              : p
          )
        );
      }
    }
    
    // Close editing mode
    setEditingPlaceId(null);
    setEditingPlaceName("");
  };

  const handleCancelEdit = () => {
    setEditingPlaceId(null);
    setEditingPlaceName("");
  };

  const handlePhotoToggle = (photoId) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const handleLoadExistingSession = async (sessionId) => {
    try {
      setCurrentStep("review");
      setSessionId(sessionId);
      
      // Load photos from the existing session
      const places = await fetchSessionPhotos(sessionId);
      setExtractedPlaces(places);
      
      // Initialize selected places with all places
      const initialSelectedPlaces = new Set(places.map(place => place.id));
      setSelectedPlaces(initialSelectedPlaces);
      
      // Hide the existing sessions modal
      setShowExistingSessions(false);
      
      console.log(`✅ Loaded existing session ${sessionId} with ${places.length} photos`);
    } catch (error) {
      console.error('❌ Error loading existing session:', error);
    }
  };

  const handleSessionSelection = (sessionId) => {
    setSelectedSessionsToDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleDeleteSelectedSessions = async () => {
    if (selectedSessionsToDelete.size === 0) return;
    
    setIsDeletingSessions(true);
    try {
      const sessionIds = Array.from(selectedSessionsToDelete);
      
      // Call API to delete sessions
      const response = await fetch(`${API_URL}/api/recommendations/photo-import/delete-sessions`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionIds,
          userEmail
        })
      });

      if (response.ok) {
        // Remove deleted sessions from the list
        setExistingSessions(prev => prev.filter(session => !selectedSessionsToDelete.has(session.id)));
        setSelectedSessionsToDelete(new Set());
        console.log(`✅ Deleted ${sessionIds.length} sessions`);
      } else {
        const errorData = await response.json();
        console.error('❌ Error deleting sessions:', errorData.message);
      }
    } catch (error) {
      console.error('❌ Error deleting sessions:', error);
    } finally {
      setIsDeletingSessions(false);
    }
  };

  const steps = [
    { key: "upload", label: "Upload", icon: PhotoIcon },
    { key: "processing", label: "Process", icon: SparklesIcon },
    { key: "review", label: "Review", icon: OutlineStarIcon },
    { key: "complete", label: "Complete", icon: CheckIcon },
  ];

  return (
    <div className="photo-import-container">
      <div className="photo-import-content">
        {/* Progress Steps - Smaller and positioned right below header */}
        <div className="progress-steps-compact">
          <div className="steps-container">
            <div className="step-connector"></div>
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.key === currentStep;
              const isCompleted = steps.findIndex(s => s.key === currentStep) > index;
              const isPending = steps.findIndex(s => s.key === currentStep) < index;

              return (
                <div key={step.key} className="step-item">
                  <div className={`step-icon-compact ${isActive ? 'active' : isCompleted ? 'completed' : 'pending'}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className={`step-label-compact ${isActive ? 'active' : isCompleted ? 'completed' : 'pending'}`}>
                    {step.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Existing Sessions Modal */}
        {showExistingSessions && existingSessions.length > 0 && (
          <div className="existing-sessions-modal">
            <div className="existing-sessions-content">
              <button 
                onClick={() => setShowExistingSessions(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
              <h3>Continue Previous Work?</h3>
              <p>You have {existingSessions.length} session{existingSessions.length !== 1 ? 's' : ''} with photos ready for submission or needing location input.</p>
              
              <div className="existing-sessions-list">
                {existingSessions.map((session) => (
                  <div key={session.id} className="existing-session-item">
                    <div className="session-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSessionsToDelete.has(session.id)}
                        onChange={() => handleSessionSelection(session.id)}
                        id={`session-${session.id}`}
                      />
                      <label htmlFor={`session-${session.id}`}></label>
                    </div>
                    <div className="session-info">
                      <p className="session-date">{formatDate(session.created_at)}</p>
                      <p className="session-photos">{session.remaining_photos} photo{session.remaining_photos !== 1 ? 's' : ''} ready for review</p>
                    </div>
                    <button 
                      onClick={() => handleLoadExistingSession(session.id)}
                      className="load-session-button"
                    >
                      Continue
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="existing-sessions-actions">
                {selectedSessionsToDelete.size > 0 && (
                  <button 
                    onClick={handleDeleteSelectedSessions}
                    disabled={isDeletingSessions}
                    className="delete-sessions-button"
                  >
                    {isDeletingSessions ? 'Deleting...' : `Delete ${selectedSessionsToDelete.size} Session${selectedSessionsToDelete.size !== 1 ? 's' : ''}`}
                  </button>
                )}
                <button 
                  onClick={() => setShowExistingSessions(false)}
                  className="start-new-button"
                >
                  Start New Upload
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Step */}
        {currentStep === "upload" && (
          <div className="upload-step">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="file-input"
              ref={fileInputRef}
            />

            <div 
              className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
              ref={dropzoneRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="dropzone-content">
                <div className="dropzone-icon">
                  <CameraIcon className="h-12 w-12" />
                </div>
                <div className="dropzone-text">
                  <h3 className="dropzone-title">Choose Photos</h3>
                  <p className="dropzone-subtitle">Select photos containing places you'd recommend to friends</p>
                  <p className="dropzone-hint">Supports up to 10,000 photos • JPG, PNG, HEIC</p>
                </div>
              </div>
            </div>

            {selectedFiles.length > 0 && (
              <div className="selected-files-card">
                <p>{selectedFiles.length} photos selected</p>
                <button 
                  onClick={handleUpload} 
                  disabled={isProcessing}
                  className="upload-button"
                >
                  {isProcessing ? 'Processing...' : 'Next'}
                  <ArrowRightIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Processing Step */}
        {currentStep === "processing" && (
          <div className="processing-step">
            <div className="processing-icon">
              <SparklesIcon className="h-12 w-12" />
            </div>
            <h2 className="processing-title">Processing Your Photos</h2>
            <p className="processing-description">
              We're analyzing your photos to extract location data and identify potential recommendations.
            </p>

            <div className="progress-container">
              <div className="progress-item">
                <div className="progress-header">
                  <span>Uploading photos</span>
                  <span>{Math.round(uploadProgress)}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>

              <div className="progress-item">
                <div className="progress-header">
                  <span>Extracting location data</span>
                  <span>{Math.round(processingProgress)}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${processingProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="processing-time">
              This usually takes 30-60 seconds depending on the number of photos
            </div>
          </div>
        )}

        {/* Review Step */}
        {currentStep === "review" && (
          <div className="review-step">
            <div className="review-header">
              <SparklesIcon className="intro-icon mini" />
              <span>Found {extractedPlaces.filter((p) => !p.isDuplicate).length} New Recommendations</span>
            </div>

            {/* Global Visibility Controls */}
            <div className="global-visibility-controls">
              <h3>Visibility Settings</h3>
              <p>Set the default visibility for all recommendations, or customize individually below.</p>
              
              <div className="visibility-options">
                <label className="visibility-option">
                  <input
                    type="radio"
                    name="globalVisibility"
                    value="public"
                    checked={globalVisibility === 'public'}
                    onChange={(e) => setGlobalVisibility(e.target.value)}
                  />
                  <span>Public - Visible to everyone</span>
                </label>
                
                <label className="visibility-option">
                  <input
                    type="radio"
                    name="globalVisibility"
                    value="connections"
                    checked={globalVisibility === 'connections'}
                    onChange={(e) => setGlobalVisibility(e.target.value)}
                  />
                  <span>Full Trust Circle - Share with all your communities</span>
                </label>
                
                <label className="visibility-option">
                  <input
                    type="radio"
                    name="globalVisibility"
                    value="communities"
                    checked={globalVisibility === 'communities'}
                    onChange={(e) => {
                      setGlobalVisibility(e.target.value);
                      if (e.target.value === 'communities' && userTrustCircles.length === 0 && !trustCirclesLoading && !trustCirclesError) {
                        fetchTrustCircles();
                      }
                    }}
                  />
                  <span>Specific Trust Circles - Choose which communities to share with</span>
                </label>
                
                <label className="visibility-option">
                  <input
                    type="radio"
                    name="globalVisibility"
                    value="per-recommendation"
                    checked={globalVisibility === 'per-recommendation'}
                    onChange={(e) => {
                      setGlobalVisibility(e.target.value);
                      if (e.target.value === 'per-recommendation' && userTrustCircles.length === 0 && !trustCirclesLoading && !trustCirclesError) {
                        fetchTrustCircles();
                      }
                    }}
                  />
                  <span>Specific to each Recommendation - Choose visibility per recommendation below</span>
                </label>
              </div>

              {/* Trust Circle Selection (only show if communities is selected) */}
              {globalVisibility === 'communities' && (
                <div className="trust-circle-selection">
                  <h4>Select Trust Circles to Share With:</h4>
                  
                  {trustCirclesLoading && (
                    <div className="loading-trust-circles">
                      <span>🔄</span>
                      Loading your communities...
                    </div>
                  )}
                  
                  {trustCirclesError && (
                    <div className="error-trust-circles">
                      <span>❌</span>
                      {trustCirclesError}
                      <button 
                        className="retry-button"
                        onClick={fetchTrustCircles}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  
                  {!trustCirclesLoading && !trustCirclesError && userTrustCircles.length > 0 && (
                    <div className="trust-circle-options">
                      {userTrustCircles.map((trustCircle) => (
                        <label key={trustCircle.id} className="trust-circle-option">
                          <input
                            type="checkbox"
                            checked={globalTrustCircleIds.includes(trustCircle.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setGlobalTrustCircleIds([...globalTrustCircleIds, trustCircle.id]);
                              } else {
                                setGlobalTrustCircleIds(globalTrustCircleIds.filter(id => id !== trustCircle.id));
                              }
                            }}
                          />
                          <span>{trustCircle.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {!trustCirclesLoading && !trustCirclesError && userTrustCircles.length === 0 && (
                    <div className="no-trust-circles-message">
                      You are not part of any communities, or we couldn't load them.
                      <a href="/trustcircles" target="_blank">Manage your Trust Circles</a>
                      or try again.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="places-container">
              {extractedPlaces.length === 0 ? (
                <div className="no-places-found">
                  <div className="no-places-content">
                    <SparklesIcon className="no-places-icon" />
                    <h3>No Places Found</h3>
                    <p>We couldn't find any businesses in your photos. This could be because:</p>
                    <ul>
                      <li>Photos don't contain location data (GPS)</li>
                      <li>Photos don't contain recognizable businesses</li>
                      <li>Processing is still in progress</li>
                    </ul>
                    <button 
                      onClick={() => setCurrentStep("upload")}
                      className="upload-button"
                    >
                      <PhotoIcon className="h-5 w-5" />
                      Try Different Photos
                    </button>
                  </div>
                </div>
              ) : (
                extractedPlaces.map((place, index) => (
                  <div
                    key={place.id}
                    className={`place-card ${place.isDuplicate ? 'duplicate' : ''} ${selectedPlaces.has(place.id) ? 'selected' : ''} ${place.needsLocationInput ? 'needs-location' : ''}`}
                  >
                    <div className="place-content">
                      <div className="place-image">
                        <img
                          src={place.photos[0]?.url || "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNjBDMTEwLjQ1NyA2MCAxMTkgNjguNTQzIDEyMCA3OVYxMjFDMTIwIDEzMS40NTcgMTEwLjQ1NyAxNDAgMTAwIDE0MEM4OS41NDMgMTQwIDgwIDEzMS40NTcgODAgMTIxVjc5QzgwIDY4LjU0MyA4OS41NDMgNjAgMTAwIDYwWiIgZmlsbD0iIzlDQTBBNiIvPgo8L3N2Zz4K"}
                          alt={place.name}
                          loading="lazy"
                          onLoad={() => console.log(`[DEBUG] Image loaded successfully for ${place.name}: ${place.photos[0]?.url}`)}
                          onError={(e) => {
                            console.log(`[DEBUG] Image failed to load for ${place.name}: ${place.photos[0]?.url}`);
                            // Prevent infinite loop by not setting the same src
                            if (!e.target.src.includes('data:image/svg+xml')) {
                              e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNjBDMTEwLjQ1NyA2MCAxMTkgNjguNTQzIDEyMCA3OVYxMjFDMTIwIDEzMS40NTcgMTEwLjQ1NyAxNDAgMTAwIDE0MEM4OS41NDMgMTQwIDgwIDEzMS40NTcgODAgMTIxVjc5QzgwIDY4LjU0MyA4OS41NDMgNjAgMTAwIDYwWiIgZmlsbD0iIzlDQTBBNiIvPgo8L3N2Zz4K";
                            }
                          }}
                        />
                        {/* Photo toggle overlay */}
                        {place.photos && place.photos.length > 0 && (
                          <div className="photo-toggle-overlay">
                            <button
                              type="button"
                              className={`photo-toggle-btn ${selectedPhotos.has(place.photoId) ? 'selected' : 'deselected'}`}
                              onClick={() => handlePhotoToggle(place.photoId)}
                              title={selectedPhotos.has(place.photoId) ? 'Include photo in recommendation' : 'Exclude photo from recommendation'}
                            >
                              {selectedPhotos.has(place.photoId) ? '✓' : '✕'}
                            </button>
                            <div className="photo-toggle-label">
                              {selectedPhotos.has(place.photoId) ? 'Include' : 'Exclude'}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="place-details">
                        <div className="place-header">
                          <div>
                            {editingPlaceId === place.id ? (
                              <div className="place-edit-container">
                                <PlaceAutocompleteInput
                                  value={editingPlaceName}
                                  onChange={handlePlaceNameChange}
                                  onPlaceSelect={handlePlaceSelect}
                                  placeholder="Search for a place..."
                                  className="place-edit-input"
                                />
                                <div className="place-edit-actions">
                                  <button 
                                    className="place-edit-cancel"
                                    onClick={handleCancelEdit}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="place-name-container">
                                <h4 className="place-name">{place.name}</h4>
                                <button 
                                  className="place-edit-button"
                                  onClick={() => handleEditPlace(place.id)}
                                  title="Edit place name"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            {place.dateOfRecommendation && (
                              <div className="place-date">
                                Visited: {formatDate(place.dateOfRecommendation)}
                              </div>
                            )}
                          </div>

                          {place.isDuplicate && (
                            <div className="place-duplicate">
                              <ExclamationTriangleIcon className="h-4 w-4" />
                              {place.existingReview ? "Already reviewed" : "Duplicate"}
                            </div>
                          )}
                        </div>

                        <div className="place-address">
                          <MapPinIcon className="h-4 w-4" />
                          {place.address}
                        </div>

                        {!place.isDuplicate && (
                          <div className="place-actions">
                            {/* Special handling for photos that need location input */}
                            {place.needsLocationInput ? (
                              <div className="location-input-section">
                                <div className="place-checkbox">
                                  <input
                                    type="checkbox"
                                    id={`place-${place.id}`}
                                    checked={selectedPlaces.has(place.id)}
                                    onChange={() => handlePlaceToggle(place.id)}
                                  />
                                  <label htmlFor={`place-${place.id}`}>Add location and recommend</label>
                                </div>
                                
                                {selectedPlaces.has(place.id) && (
                                  <div className="location-input-container">
                                    <div className="rating-section">
                                      <label className="star-rating-label">Add Location *</label>
                                      <PlaceAutocompleteInput
                                        value=""
                                        onChange={() => {}} // Will be handled by onPlaceSelect
                                        onPlaceSelect={(selectedPlace) => handlePlaceSelect({...selectedPlace, photoId: place.id})}
                                        placeholder="Search for the place in this photo..."
                                        className="location-input"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="place-checkbox">
                                <input
                                  type="checkbox"
                                  id={`place-${place.id}`}
                                  checked={selectedPlaces.has(place.id)}
                                  onChange={() => handlePlaceToggle(place.id)}
                                />
                                <label htmlFor={`place-${place.id}`}>Recommend this place</label>
                              </div>
                            )}



                            {selectedPlaces.has(place.id) && (
                              <div className="place-review">
                                <div className="rating-section">
                                  <label className="star-rating-label">Your Rating *</label>
                                  <div className="star-rating">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <StarDisplay
                                        key={star}
                                        active={star <= (reviews[place.id]?.rating || 0)}
                                        onClick={() => handleRatingChange(place.id, star)}
                                      />
                                    ))}
                                  </div>
                                </div>

                                <div className="rating-section">
                                  <label className="star-rating-label">Your recommendation (optional)</label>
                                  <textarea
                                    placeholder="What did you love about this place? Share your experience... (optional - you can add this later)"
                                    value={reviews[place.id]?.message || ""}
                                    onChange={(e) => handleMessageChange(place.id, e.target.value)}
                                    onClick={(e) => {
                                      if (e.target.value === "" && e.target.placeholder) {
                                        e.target.placeholder = "";
                                      }
                                    }}
                                    className="review-textarea"
                                    rows="2"
                                  />
                                  <button 
                                    className="edit-recommendation-link"
                                    onClick={() => handleMessageChange(place.id, "")}
                                  >
                                    Clear recommendation
                                  </button>
                                </div>

                                {/* Individual Visibility Controls - only show if global visibility is "per-recommendation" */}
                                {globalVisibility === 'per-recommendation' && (
                                  <div className="individual-visibility-section">
                                    <label className="star-rating-label">Share with</label>
                                    <div className="individual-visibility-options">
                                      <label className="individual-visibility-option">
                                        <input
                                          type="radio"
                                          name={`visibility-${place.id}`}
                                          value="public"
                                          checked={(individualVisibilities[place.id] || 'public') === 'public'}
                                          onChange={(e) => {
                                            setIndividualVisibilities({
                                              ...individualVisibilities,
                                              [place.id]: e.target.value
                                            });
                                            // Clear trust circles when switching to public
                                            setIndividualTrustCircleIds({
                                              ...individualTrustCircleIds,
                                              [place.id]: []
                                            });
                                          }}
                                        />
                                        <span>Public - Visible to everyone</span>
                                      </label>
                                      
                                      <label className="individual-visibility-option">
                                        <input
                                          type="radio"
                                          name={`visibility-${place.id}`}
                                          value="connections"
                                          checked={(individualVisibilities[place.id] || 'public') === 'connections'}
                                          onChange={(e) => {
                                            setIndividualVisibilities({
                                              ...individualVisibilities,
                                              [place.id]: e.target.value
                                            });
                                            // Clear trust circles when switching to connections
                                            setIndividualTrustCircleIds({
                                              ...individualTrustCircleIds,
                                              [place.id]: []
                                            });
                                          }}
                                        />
                                        <span>Full Trust Circle - Share with all your communities</span>
                                      </label>
                                      
                                      <label className="individual-visibility-option">
                                        <input
                                          type="radio"
                                          name={`visibility-${place.id}`}
                                          value="communities"
                                          checked={(individualVisibilities[place.id] || 'public') === 'communities'}
                                          onChange={(e) => {
                                            setIndividualVisibilities({
                                              ...individualVisibilities,
                                              [place.id]: e.target.value
                                            });
                                            // Fetch trust circles if not already loaded
                                            if (userTrustCircles.length === 0 && !trustCirclesLoading && !trustCirclesError) {
                                              fetchTrustCircles();
                                            }
                                          }}
                                        />
                                        <span>Specific Trust Circles - Choose which communities</span>
                                      </label>
                                    </div>

                                    {/* Individual Trust Circle Selection */}
                                    {(individualVisibilities[place.id] || 'public') === 'communities' && (
                                      <div className="individual-trust-circle-selection">
                                        <h5>Select Trust Circles:</h5>
                                        
                                        {trustCirclesLoading && (
                                          <div className="loading-trust-circles">
                                            <span>🔄</span>
                                            Loading your communities...
                                          </div>
                                        )}
                                        
                                        {trustCirclesError && (
                                          <div className="error-trust-circles">
                                            <span>❌</span>
                                            {trustCirclesError}
                                            <button 
                                              className="retry-button"
                                              onClick={fetchTrustCircles}
                                            >
                                              Retry
                                            </button>
                                          </div>
                                        )}
                                        
                                        {!trustCirclesLoading && !trustCirclesError && userTrustCircles.length > 0 && (
                                          <div className="individual-trust-circle-options">
                                            {userTrustCircles.map((trustCircle) => (
                                              <label key={trustCircle.id} className="individual-trust-circle-option">
                                                <input
                                                  type="checkbox"
                                                  checked={(individualTrustCircleIds[place.id] || []).includes(trustCircle.id)}
                                                  onChange={(e) => {
                                                    const currentIds = individualTrustCircleIds[place.id] || [];
                                                    if (e.target.checked) {
                                                      setIndividualTrustCircleIds({
                                                        ...individualTrustCircleIds,
                                                        [place.id]: [...currentIds, trustCircle.id]
                                                      });
                                                    } else {
                                                      setIndividualTrustCircleIds({
                                                        ...individualTrustCircleIds,
                                                        [place.id]: currentIds.filter(id => id !== trustCircle.id)
                                                      });
                                                    }
                                                  }}
                                                />
                                                <span>{trustCircle.name}</span>
                                              </label>
                                            ))}
                                          </div>
                                        )}
                                        
                                        {!trustCirclesLoading && !trustCirclesError && userTrustCircles.length === 0 && (
                                          <div className="no-trust-circles-message">
                                            You are not part of any communities, or we couldn't load them.
                                            <a href="/trustcircles" target="_blank">Manage your Trust Circles</a>
                                            or try again.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {selectedPlaces.size > 0 && (
              <div className="submit-card">
                <div className="submit-content">
                  <div className="submit-info">
                    <h3>Ready to submit</h3>
                    <p>{selectedPlaces.size} recommendation{selectedPlaces.size !== 1 ? "s" : ""} selected</p>
                    <p className="photo-summary">
                      📸 {selectedPhotos.size} photo{selectedPhotos.size !== 1 ? "s" : ""} will be included
                    </p>
                    <p className="optional-note">
                      💡 Messages are optional - you can add details later
                    </p>
                  </div>
                  <button 
                    onClick={(e) => {
                      console.log('Button clicked!', { canProceed, disabled: !canProceed });
                      if (canProceed) {
                        handleSubmit();
                      }
                    }} 
                    disabled={!canProceed}
                    className={`submit-button ${canProceed ? 'enabled' : 'disabled'}`}
                  >
                    <UsersIcon className="h-5 w-5" />
                    Share Recommendations
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Complete Step */}
        {currentStep === "complete" && (
          <div className="complete-step">
            <div className="complete-icon">
              <CheckIcon className="h-12 w-12" />
            </div>
            <h2 className="complete-title">Recommendations Added!</h2>
            <p className="complete-description">
              Your {submissionResult ? submissionResult.submittedRecommendations?.length : selectedPlaces.size} recommendation{submissionResult ? (submissionResult.submittedRecommendations?.length !== 1 ? "s have" : " has") : (selectedPlaces.size !== 1 ? "s have" : " has")} been added to
              your trust circle{submissionResult?.submittedRecommendations?.some(rec => !rec.isComplete) ? " (some without messages - you can add details later)" : ""}.
            </p>

            <div className="complete-actions">
              <button 
                onClick={() => setCurrentStep("upload")}
                className="complete-button secondary"
              >
                <PhotoIcon className="h-5 w-5" />
                Add More Photos
              </button>
              <button className="complete-button primary">
                <UsersIcon className="h-5 w-5" />
                View My Recommendations
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoImportFlow; 